import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export default function MarkerCarousel({ markers, currentTime, onMarkerClick }) {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(4)
    const ITEM_WIDTH = 172 // 160px + 12px gap

    // Calculate items per page based on window width
    useEffect(() => {
        const calculateItemsPerPage = () => {
            if (typeof window === 'undefined') {
                setItemsPerPage(4)
                return
            }

            const width = window.innerWidth
            const items = Math.floor(width / ITEM_WIDTH)
            setItemsPerPage(Math.max(2, Math.min(items, 12)))
        }

        calculateItemsPerPage()
        window.addEventListener('resize', calculateItemsPerPage)

        return () => window.removeEventListener('resize', calculateItemsPerPage)
    }, [])

    if (!markers || markers.length === 0) {
        return <div className="text-gray-500 text-center py-8">Keine Folien verfügbar</div>
    }

    // Calculate max scroll position
    const totalPages = Math.ceil(markers.length / itemsPerPage)
    const maxIndex = Math.max(0, totalPages - 1)

    const handlePrev = () => {
        setCurrentIndex(prev => Math.max(0, prev - 1))
    }

    const handleNext = () => {
        setCurrentIndex(prev => Math.min(maxIndex, prev + 1))
    }

    // Calculate offset for smooth scroll
    const scrollOffset = currentIndex * ITEM_WIDTH * itemsPerPage

    return (
        <div className="w-full">
            {/* Carousel Container */}
            <div className="flex items-center justify-between gap-4">
                {/* Prev Button */}
                <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Vorherige Folien"
                >
                    <ChevronLeft size={32} className="text-green-700" strokeWidth={2} />
                </button>

                {/* Markers Container - Smooth Scroll */}
                <div className="flex-1 overflow-hidden">
                    <div
                        className="flex gap-3 transition-transform duration-700 ease-in-out"
                        style={{
                            transform: `translateX(-${scrollOffset}px)`
                        }}
                    >
                        {markers.map((marker, idx) => (
                            <div
                                key={marker.id}
                                className={`
                  border-2 rounded-lg overflow-hidden cursor-pointer transition-all duration-300
                  flex-shrink-0
                  ${currentTime >= marker.time_seconds && currentTime < (markers[idx + 1]?.time_seconds || Infinity)
                                        ? 'border-green-700 shadow-lg scale-105'
                                        : 'border-gray-300 hover:border-green-500'
                                    }
                `}
                                style={{
                                    width: '160px',
                                    height: '120px'
                                }}
                                onClick={() => onMarkerClick(marker.time_seconds)}
                                title={`Folie ${marker.slide_no} - ${Math.floor(marker.time_seconds / 60)}:${String(Math.floor(marker.time_seconds % 60)).padStart(2, '0')}`}
                            >
                                {marker.image_path ? (
                                    <img
                                        src={`/content/${marker.image_path}`}
                                        alt={`Folie ${marker.slide_no}`}
                                        className="w-full h-full object-contain bg-gray-100"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500 text-sm text-center">Folie {marker.slide_no}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Next Button */}
                <button
                    onClick={handleNext}
                    disabled={currentIndex === totalPages - 1}
                    className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Nächste Folien"
                >
                    <ChevronRight size={32} className="text-green-700" strokeWidth={2} />
                </button>
            </div>
        </div>
    )
}